import { useState, useEffect } from "react";
import { Wallet, ArrowRightLeft, Route as RouteIcon, Shield, AlertTriangle, AlertOctagon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import AdminLayout from "./AdminLayout";
import axios from "axios";
import { API } from "../../App";

const StatCard = ({ title, value, icon: Icon, color = "slate" }) => {
  const colorClasses = {
    slate: "bg-slate-100 text-slate-600",
    emerald: "bg-emerald-100 text-emerald-600",
    amber: "bg-amber-100 text-amber-600",
    rose: "bg-rose-100 text-rose-600",
    blue: "bg-blue-100 text-blue-600",
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{title}</p>
            <p className="text-3xl font-bold text-slate-900">{value}</p>
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminDashboard = ({ user }) => {
  const [stats, setStats] = useState({
    addresses: 0,
    transactions: 0,
    routes: 0,
    risk_breakdown: { low: 0, medium: 0, high: 0 }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
        setStats(response.data);
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  return (
    <AdminLayout user={user}>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Добро пожаловать, {user?.name?.split(" ")[0] || "Admin"}!
          </h1>
          <p className="text-slate-500">
            Панель управления блокчейн-обозревателя ChainScope
          </p>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard 
            title="Адреса" 
            value={loading ? "..." : stats.addresses} 
            icon={Wallet} 
            color="blue" 
          />
          <StatCard 
            title="Транзакции" 
            value={loading ? "..." : stats.transactions} 
            icon={ArrowRightLeft} 
            color="slate" 
          />
          <StatCard 
            title="Маршруты" 
            value={loading ? "..." : stats.routes} 
            icon={RouteIcon} 
            color="slate" 
          />
        </div>

        {/* Risk Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Распределение по рискам</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-emerald-600 font-medium">Низкий риск</p>
                  <p className="text-2xl font-bold text-emerald-700">
                    {loading ? "..." : stats.risk_breakdown.low}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-amber-600 font-medium">Средний риск</p>
                  <p className="text-2xl font-bold text-amber-700">
                    {loading ? "..." : stats.risk_breakdown.medium}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-rose-50 rounded-xl border border-rose-200">
                <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                  <AlertOctagon className="w-6 h-6 text-rose-600" />
                </div>
                <div>
                  <p className="text-sm text-rose-600 font-medium">Высокий риск</p>
                  <p className="text-2xl font-bold text-rose-700">
                    {loading ? "..." : stats.risk_breakdown.high}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Быстрые действия</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a 
                href="/admin/addresses" 
                className="p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                data-testid="quick-add-address"
              >
                <Wallet className="w-5 h-5 text-slate-600 mb-2" />
                <h3 className="font-medium text-slate-900">Добавить адрес</h3>
                <p className="text-sm text-slate-500">Создать новый адрес для отслеживания</p>
              </a>

              <a 
                href="/admin/transactions" 
                className="p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                data-testid="quick-add-tx"
              >
                <ArrowRightLeft className="w-5 h-5 text-slate-600 mb-2" />
                <h3 className="font-medium text-slate-900">Добавить транзакцию</h3>
                <p className="text-sm text-slate-500">Записать новую транзакцию</p>
              </a>

              <a 
                href="/admin/routes" 
                className="p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                data-testid="quick-add-route"
              >
                <RouteIcon className="w-5 h-5 text-slate-600 mb-2" />
                <h3 className="font-medium text-slate-900">Создать маршрут</h3>
                <p className="text-sm text-slate-500">Визуализировать поток средств</p>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
